import { Flex, VStack } from '@chakra-ui/react'
import { t } from 'i18next'
import { MailOpen } from 'lucide-react'
import { concat, equals, propEq, filter as ramdaFilter } from 'ramda'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { useModalState } from '~/atoms/ModalState'
import { useUserState } from '~/atoms/UserState'
import {
  DotNotifier,
  FilterWrapper,
  ItemWrapper,
  ListWrapper,
  NotificationWrapper,
  StyledItemWrapper,
  UnreadWrapper,
} from '~/components/compositional/ListViewStyles'
import { FilterMenu, Text } from '~/components/core'
import { FontStyle } from '~/components/core/Text'
import EmptySearch from '~/components/EmptySearch'
import { EmptyState } from '~/components/EmptyState'
import { ToggleListButton } from '~/components/layout/ResponsivePage'
import ListItem from '~/components/ListItem'
import { QuickActionButtonVariants } from '~/components/shared/componentStyles/QuickActionButton.style'
import {
  DropdownSelect,
  SelectItem,
} from '~/components/shared/Dropdown/DropdownSelect'
import { MenuListItem } from '~/components/shared/Dropdown/MenuItem'
import { LoadingSpinner } from '~/components/shared/LoadingSpinner'
import { QuickActionButton } from '~/components/shared/QuickActionButton'
import { ScrollingList } from '~/components/shared/ScrollingList'
import { SearchInput } from '~/components/shared/SearchInput'
import { ChatFilters, ChatOptions } from '~/constants/config'
import { useAnalytics } from '~/hooks/useAnalytics'
import { useDebounce } from '~/hooks/useDebounce'
import { useEmitterService } from '~/hooks/useEmitterService'
import {
  MessagingEmitterEventType,
  ThreadCreatedPayload,
} from '~/services/EventEmitter/types'
import { AnalyticsNamespace, MessagingAnalyticsEvents } from '~/types/Analytics'
import { SearchValue } from '~/types/Shared'
import { hasValue } from '~/utils/common'
import { findHighlightChunks } from '~/utils/highlightChunks'
import { useMessageGroupsViewModel } from '~/viewModels/Messaging/MessageGroupsViewModel'
import { useMessageThreadsViewModel } from '~/viewModels/Messaging/MessageThreadsViewModel'

import { formatDateString } from '@mangovoice/core'
import { MessageGroup, MessageThread } from '@mangovoice/types'

import {
  NameWrapper,
  StyledSubtitleText,
  StyledTitleText,
  TitleMatch,
} from './Messaging.styles'

const FILTER_CHANGE_DEBOUNCE_MS = 200
const SEARCH_INPUT_DEBOUNCE_MS = 300
const LoadingWrapper = styled.div`
  display: flex;
  height: 100%;
  align-items: center;
  justify-content: center;
`

interface MessageThreadsListViewProps {
  onMessageThreadSelected: (
    thread: MessageThread,
    messageGroup?: MessageGroup,
  ) => void
  pendingThreadId?: number
  selectedThreadId?: number
}
const MessageThreadsListView = ({
  onMessageThreadSelected,
  pendingThreadId,
  selectedThreadId,
}: MessageThreadsListViewProps) => {
  const { track } = useAnalytics(AnalyticsNamespace.MESSAGING)
  const [visibleMessageThreads, setVisibleMessageThreads] =
    useState<MessageThread[]>()
  const [showLoader, setShowLoader] = useState(true)
  const [firstLoadStarted, setFirstLoadStarted] = useState(false)
  const [unreadsFilter, setUnreadsFilter] = useState<number>(ChatFilters.RECENT)
  const [debounceSearch, setDebounceSearch] = useState<SearchValue>(
    SearchValue.empty,
  )
  const { pbxId } = useUserState()
  const searchInputRef = useRef<HTMLInputElement>(null)
  const { userMessageGroups } = useMessageGroupsViewModel()
  const {
    messagingModal: [messagingModalState, setMessagingModalState],
  } = useModalState()
  const [pendingAutoSelectedThreadId, setPendingAutoSelectedThreadId] =
    useState<number>()

  const [selectedGroup, setSelectedGroup] = useState(0)

  const { subscribe: emitterSubscribe, emit } = useEmitterService()

  const {
    messageThreadsLoading,
    selectedThread,
    messageThreads,
    setFilters,
    tryGetNextThreadPage,
    markMessageThreadRead,
    setSelectedThread,
  } = useMessageThreadsViewModel()

  const {
    messagingMarkAllReadModal: [, setChatMarkAllReadModalState],
  } = useModalState()

  const unreadThreads = visibleMessageThreads?.filter(
    (thread) => thread.unread_message_count > 0,
  )
  const data = equals(unreadsFilter ?? 0, ChatFilters.RECENT)
    ? visibleMessageThreads
    : unreadThreads

  const threads =
    selectedGroup === 0
      ? data
      : data?.filter((thread) => thread.message_group_id === selectedGroup)

  const groups = userMessageGroups ?? []
  const groupJsxCache = useMemo(
    () =>
      groups.map((group) => {
        const { id, name, phone_numbers, unread_indicator_count } = group
        const unreadCounts =
          unread_indicator_count > 99 ? '99+' : unread_indicator_count
        const pNums = ramdaFilter(propEq(true, 'is_primary_sms'))(
          phone_numbers as any,
        )
        const unreadIndicator =
          unread_indicator_count > 0 ? (
            <UnreadWrapper>{unreadCounts}</UnreadWrapper>
          ) : undefined
        const label = (
          <MenuListItem
            primaryText={name}
            secondaryText={pNums[0]?.number_for_display}
            rightContent={unreadIndicator}
          />
        )
        return { value: id, content: label, selectedLabel: name }
      }),
    [userMessageGroups, selectedGroup],
  )

  const groupOptions = concat(
    [{ value: 0, content: t('messageThreads.allGroups') }] as any,
    groupJsxCache,
  ) as unknown as SelectItem<string | number>[]

  const unselectedGroups = ramdaFilter(
    (x: { id: number }) => x.id !== selectedGroup,
  )(groups)
  let unreads = 0
  unselectedGroups.forEach((group) => {
    unreads += group.unread_indicator_count
  })

  const tryGetThreadMessageGroup = useCallback(
    (thread: MessageThread) => {
      return userMessageGroups?.find((mg) => mg.id === thread.message_group_id)
    },
    [userMessageGroups],
  )

  const handleSelectMessageThread = useCallback(
    (thread: MessageThread) => {
      setSelectedThread(thread)
      const messageGroup = tryGetThreadMessageGroup(thread)
      onMessageThreadSelected(thread, messageGroup)
    },
    [setSelectedThread, onMessageThreadSelected, tryGetThreadMessageGroup],
  )

  const handleClickMessageThreadItem = (item: MessageThread) => () => {
    markMessageThreadRead(item)
    handleSelectMessageThread(item)
    item.unread_message_count = 0 //mark the thread read locally
    // No external_phone_number here — phone numbers are PHI.
    track(MessagingAnalyticsEvents.ThreadSelected, {
      id: item.id,
      group_id: item.message_group_id,
    })
  }

  const handleSetUnreadsFilter = (e: number) => {
    setUnreadsFilter(e)
  }

  const handleSetSearchFilter = (searchValue: SearchValue) => {
    setDebounceSearch(searchValue)
  }

  const [searchValue, setSearchValue] = useState<SearchValue>(SearchValue.empty)

  useDebounce(
    () => {
      setSearchValue(debounceSearch)
    },
    SEARCH_INPUT_DEBOUNCE_MS,
    [debounceSearch],
  )

  const handleSelectGroup = (id: number) => {
    if (selectedGroup !== id) {
      setSelectedGroup(id)
    }
  }

  const handleGetNextThreadPage = () => {
    tryGetNextThreadPage()
  }

  const handleCreateNewChat = () => {
    track(MessagingAnalyticsEvents.NewThreadModalOpened)
    setMessagingModalState({ ...messagingModalState, open: true })
  }

  const handleOpenMarkReadModal = () => {
    setChatMarkAllReadModalState({
      open: true,
    })
  }

  function getTextingSubtitleText(selectedFilter: number) {
    switch (selectedFilter) {
      case ChatFilters.UNREAD:
        return t('messageThreads.emptyState.subtitle1')
      default:
        return t('messageThreads.emptyState.subtitle2')
    }
  }

  const handleThreadsUpdated = (updatedThreads: MessageThread[]) => {
    // Render exactly what the view model currently has for this group.
    // Carrying over local rows can leak stale rows across PBX/group/filter changes.
    const newSortedThreads = [...updatedThreads].sort(
      (a, b) => Date.parse(b.updated_at) - Date.parse(a.updated_at),
    )
    setVisibleMessageThreads(newSortedThreads)
  }

  emitterSubscribe({
    subscriptionToken: 'message-thread-list-view:thread-created',
    subscription: {
      eventType: MessagingEmitterEventType.MESSAGE_THREAD_CREATED,
      callback: (tcPayload: ThreadCreatedPayload) => {
        setPendingAutoSelectedThreadId(tcPayload.thread.id)
      },
    },
  })

  //#endregion
  useDebounce(
    () => {
      if (searchValue.value === undefined && unreadsFilter === undefined) {
        return
      }

      //if filters change, reset the visible message threads, and set filters on the view model
      setFilters(
        searchValue.value,
        unreadsFilter === ChatFilters.UNREAD ? true : undefined,
        selectedGroup,
        true,
      )
    },
    FILTER_CHANGE_DEBOUNCE_MS,
    [searchValue, unreadsFilter, selectedGroup],
  )

  //when searchFilter or unreadsFilter changes, immediately sets showLoader to true, and resets the visibleMessageThreads state while we wait for useDebounce to hit
  useEffect(() => {
    setVisibleMessageThreads([])
    setShowLoader(true)

    return () => {
      setFilters(undefined, undefined, undefined, false)
    }
  }, [searchValue, unreadsFilter, selectedGroup])

  //#region USE EFFECTS
  // On PBX switch, force a full list reset and fetch with default filters.
  // This effect is intentionally keyed only to pbxId so ordinary filter changes
  // do not re-run PBX reset behavior. `setFilters`/`setSelectedThread` are
  // intentionally omitted from deps because their identities are view-model
  // driven and would otherwise cause unrelated re-runs.
  useEffect(() => {
    setVisibleMessageThreads([])
    setShowLoader(true)
    setFirstLoadStarted(false)
    setPendingAutoSelectedThreadId(undefined)
    setDebounceSearch(SearchValue.empty)
    setSearchValue(SearchValue.empty)
    setUnreadsFilter(ChatFilters.RECENT)
    setSelectedGroup(0)
    setFilters('', undefined, 0, true)
    setSelectedThread(undefined)

    return () => {
      //on unmount, reset the selected thread - if this is not done, the model view could still technically be mounted somewhere, and automatically mark the thread as read
      setSelectedThread(undefined)
    }
  }, [pbxId])

  //watches for changes to messageThreads, updates the visibleMessageThreads state
  useEffect(() => {
    handleThreadsUpdated(
      messageThreads.messageThreadsByGroup.get(selectedGroup) ?? [],
    )
  }, [messageThreads])

  //handles the loading state of the list
  useEffect(() => {
    if (firstLoadStarted && !messageThreadsLoading) {
      setShowLoader(false)
    }

    if (!firstLoadStarted && messageThreadsLoading) {
      setFirstLoadStarted(true)
    }

    if (
      messageThreadsLoading &&
      visibleMessageThreads &&
      visibleMessageThreads.length > 0
    ) {
      setShowLoader(true)
    }
  }, [messageThreadsLoading])

  //watches for pending thread selection, checks to see if the pending thread is in the list of threads, selects the thread, then unsets the pending thread
  useEffect(() => {
    if (!pendingAutoSelectedThreadId) {
      return
    }

    // First check visibleMessageThreads (local state)
    let thread = visibleMessageThreads?.find(
      (t) => t.id === pendingAutoSelectedThreadId,
    )

    // If not found in visible threads, search all threads in the view model
    if (!thread) {
      for (const threads of messageThreads.messageThreadsByGroup.values()) {
        thread = threads.find((t) => t.id === pendingAutoSelectedThreadId)
        if (thread) break
      }
    }

    if (!thread) {
      return
    }

    // Mark thread as read locally if it has unread messages (since it's already marked on server via popup)
    if (thread.unread_message_count > 0) {
      thread.unread_message_count = 0
    }
    handleSelectMessageThread(thread)
    setPendingAutoSelectedThreadId(undefined)

    // Emit event to update badge count in nav (thread was marked as read from popup)
    emit(MessagingEmitterEventType.MESSAGE_THREAD_MARKED_READ, {
      threadId: thread.id,
    })
  }, [
    pendingAutoSelectedThreadId,
    visibleMessageThreads,
    messageThreads,
    handleSelectMessageThread,
    emit,
  ])

  // Handle deep link pending thread ID from parent
  useEffect(() => {
    if (pendingThreadId) {
      setPendingAutoSelectedThreadId(pendingThreadId)
    }
  }, [pendingThreadId])
  //#endregion

  return (
    <VStack display="flex" height="100%" alignSelf="stretch" gap="0">
      <Flex
        width="100%"
        padding="1rem"
        justifyContent="center"
        alignItems="center"
        gap="0.75rem"
        alignSelf="stretch"
        backgroundColor="bg.default"
        borderBottom="2px solid var(--chakra-colors-border-default)"
      >
        <ToggleListButton hidden clickBehavior="always-close" />
        <SearchInput
          ref={searchInputRef}
          value={debounceSearch.label}
          placeholder={t('messageThreads.searchPlaceholder')}
          inputLabel={t('messageThreads.searchLabel', 'Search messages')}
          onChange={handleSetSearchFilter}
        />
      </Flex>
      <ItemWrapper>
        <DropdownSelect
          items={groupOptions as any}
          onSelect={handleSelectGroup}
          selectedValue={selectedGroup}
        />
      </ItemWrapper>
      {unreads > 0 && selectedGroup !== 0 && (
        <NotificationWrapper
          as="button"
          type="button"
          key={unreads}
          onClick={() => handleSelectGroup(0)}
        >
          <DotNotifier aria-hidden />
          <Text
            as="span"
            fontStyle={FontStyle.Tiny}
            text={t('messageThreads.new', { count: unreads })}
            color="var(--chakra-colors-brand-solid)"
          />
        </NotificationWrapper>
      )}
      <StyledItemWrapper
        style={{
          paddingBottom: '12px',
          borderBottom: '2px solid var(--chakra-colors-border-default)',
          height: 'auto',
        }}
      >
        <FilterWrapper>
          <FilterMenu
            selectedFilter={unreadsFilter}
            options={ChatOptions()}
            ariaLabel={t('a11y.filterGroup.text')}
            onChange={(e) => handleSetUnreadsFilter(e as number)}
          />
        </FilterWrapper>
        <QuickActionButton
          actionIcon={MailOpen}
          tooltipText={t('messageThreads.markAllRead')}
          onClick={handleOpenMarkReadModal}
          variant={QuickActionButtonVariants.Default_200}
        />
      </StyledItemWrapper>
      {threads && threads.length > 0 ? (
        <ScrollingList
          display="flex"
          flex="1"
          width="100%"
          height="70%"
          backgroundColor="bg.default"
          px=".25rem"
          py=".75rem"
          scrollbarOptions={{ showOnHoverOnly: true }}
          itemData={threads}
          getItemKey={(thread) => thread.id}
          onEndReached={handleGetNextThreadPage}
          showLoaderFooter={messageThreadsLoading}
          onBuildItemContent={(_, thread: MessageThread) => {
            const { id, updated_at, latest_message, unread_message_count } =
              thread

            const unRead = unread_message_count > 0
            const textPlaceholder =
              (latest_message?.direction ?? '') === 'inbound'
                ? t('messagingPage.textPlaceholder.receivedFile')
                : t('messagingPage.textPlaceholder.fileSent')

            const activated = equals(selectedThreadId ?? selectedThread?.id, id)

            const threadName = thread.name
            const chunks = findHighlightChunks(
              threadName,
              searchValue.value ?? '',
            )

            const titleElement = (
              <NameWrapper>
                <StyledTitleText
                  fontStyle={FontStyle.Normal}
                  color={
                    searchValue.value !== ''
                      ? 'var(--chakra-colors-fg-muted)'
                      : 'var(--chakra-colors-fg-default)'
                  }
                  weight={unRead && searchValue.value === '' ? '700' : 'normal'}
                >
                  {searchValue.value !== ''
                    ? chunks.map(({ start, end, highlight }) => {
                        const text = threadName.slice(start, end)
                        return highlight ? (
                          <TitleMatch key={start}>{text}</TitleMatch>
                        ) : (
                          <React.Fragment key={start}>{text}</React.Fragment>
                        )
                      })
                    : threadName}
                </StyledTitleText>
              </NameWrapper>
            )
            const subTitle = (
              <StyledSubtitleText
                fontStyle={FontStyle.Tiny}
                text={
                  !latest_message?.body && Array.isArray(latest_message?.files)
                    ? textPlaceholder
                    : latest_message?.body
                }
                color={
                  activated
                    ? 'var(--chakra-colors-fg-subtle)'
                    : unRead
                      ? 'var(--chakra-colors-fg-subtle)'
                      : 'var(--chakra-colors-fg-muted)'
                }
              />
            )
            return (
              <ListItem
                key={`text-item-${id}`}
                activated={activated}
                title={titleElement}
                subTitle={subTitle}
                shouldShowUnreadDot={unRead}
                date={
                  <Text
                    as="span"
                    fontStyle={FontStyle.Tiny}
                    text={formatDateString(updated_at)}
                    color="var(--chakra-colors-fg-subtle)"
                  />
                }
                onClick={handleClickMessageThreadItem(thread)}
              />
            )
          }}
        />
      ) : showLoader ? (
        <LoadingWrapper>
          <LoadingSpinner />
        </LoadingWrapper>
      ) : (
        <ListWrapper>
          {hasValue(searchValue.label) ? (
            <EmptySearch search={searchValue.value} />
          ) : (
            <EmptyState
              title={t('messageThreads.emptyState.title')}
              subtitle={`${getTextingSubtitleText(unreadsFilter)}`}
              buttonText={t('messageThreads.buttonText')}
              onClick={handleCreateNewChat}
            />
          )}
        </ListWrapper>
      )}
    </VStack>
  )
}

export default MessageThreadsListView
